import {
  createRef,
  useState,
  useContext,
  useMemo,
  useEffect,
  useCallback,
} from "react";
import moment from "moment";
import { IconButton } from "@remo-co/ui-core/src/components/IconButton";
import { Box } from "@remo-co/ui-core/src/components/Box";
import { Typography } from "@remo-co/ui-core/src/components/Typography";
import { ChevronLeft } from "@remo-co/ui-core/src/icons/ChevronLeft";
import { ChevronRight } from "@remo-co/ui-core/src/icons/ChevronRight";
import { SxProps, Theme } from "@mui/material/styles";
import { EVENT_AGENDA_CONTEXT } from "../../context/EventAgendaContext";
import { styles } from "./styles";

interface Props {
  sx?: SxProps<Theme>;
}

export const EventDaysScroller = ({ sx }: Props) => {
  const scrollRef = createRef<HTMLDivElement>();
  const [currentPage, setCurrentPage] = useState(0);
  const { eventData, state, actions } = useContext(EVENT_AGENDA_CONTEXT);
  const currentDate = state?.currentDate || null;

  const getDateString = useCallback(
    (date: moment.Moment) =>
      eventData
        ? `day-${date.diff(
            moment(eventData.startTime).set({
              hour: 0,
              minute: 0,
              second: 0,
              millisecond: 0,
            }),
            "days",
          )}`
        : "",
    [eventData],
  );

  const isSameDate = useCallback(
    (date: moment.Moment) => currentDate === getDateString(date),
    [currentDate, getDateString],
  );

  const setNewCurrentDate = useCallback(
    (date: string) => {
      const dateObj = moment(date);
      if (!isSameDate(dateObj)) {
        actions.setCurrentDate(getDateString(dateObj));
      }
    },
    [isSameDate, actions, getDateString],
  );

  const buildEventDays = useMemo(() => {
    if (eventData) {
      let newStartDate = moment(eventData.startTime).set({
        hour: 0,
        minute: 0,
        second: 0,
        millisecond: 0,
      });

      const endDate = moment(eventData.endTime).set({
        hour: 0,
        minute: 0,
        second: 0,
        millisecond: 0,
      });
      const items = [];

      while (newStartDate.diff(endDate) <= 0) {
        const dateStr = newStartDate.format("MMMM Do");
        const fullDate = newStartDate.format("YYYY-MM-DD");
        const isActive = isSameDate(newStartDate);

        const itemStyleArray: (SxProps<Theme> | false)[] = [
          styles.itemBase,
          isActive && styles.itemActiveState,
        ];
        const itemStyle: SxProps<Theme> = itemStyleArray.filter(
          Boolean,
        ) as SxProps<Theme>;

        items.push(
          <Typography
            component="div"
            key={fullDate}
            sx={itemStyle}
            onClick={() => setNewCurrentDate(fullDate)}
          >
            {dateStr}
          </Typography>,
        );
        newStartDate = moment(newStartDate).add(1, "day");
      }
      return items;
    }
    return [];
  }, [eventData, isSameDate, setNewCurrentDate]);

  const eventStartTime = eventData?.startTime;
  const eventEndTime = eventData?.endTime;

  const noOfDays = useMemo(
    () =>
      eventStartTime && eventEndTime
        ? moment(eventEndTime)
            .set({
              hour: 0,
              minute: 0,
              second: 0,
              millisecond: 0,
            })
            .diff(
              moment(eventStartTime).set({
                hour: 0,
                minute: 0,
                second: 0,
                millisecond: 0,
              }),
              "days",
            ) + 1
        : 0,
    [eventStartTime, eventEndTime],
  );

  const showPrev = () => {
    setCurrentPage(Math.max(0, currentPage - 1));
  };
  const showNext = () => {
    setCurrentPage(Math.min(currentPage + 1, Math.floor(noOfDays / 3)));
  };

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.style.transform = `translateX(-${currentPage * 100}%)`;
    }
  }, [currentPage, scrollRef]);

  return (
    <Box sx={{ ...styles.scrollerBase, ...sx }}>
      {noOfDays > 1 && (
        <IconButton
          disabled={currentPage === 0}
          sx={styles.iconButtonBase}
          onClick={showPrev}
          size="large"
        >
          <ChevronLeft />
        </IconButton>
      )}
      <Box sx={styles.itemsContainer}>
        <Box sx={styles.itemsInnerContainer} ref={scrollRef}>
          {buildEventDays}
        </Box>
      </Box>
      {noOfDays > 1 && (
        <IconButton
          disabled={currentPage >= Math.ceil(noOfDays / 3) - 1}
          sx={styles.iconButtonBase}
          onClick={showNext}
          size="large"
        >
          <ChevronRight />
        </IconButton>
      )}
    </Box>
  );
};
